import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { getQuizQuestions } from "../../lib/quiz";
import { submitQuiz } from "../../lib/api";
import { ArrowRight, CheckCircle, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useI18n } from "../../i18n/I18nContext";

export const QuizModal = ({ open, onOpenChange, onWantDemo }) => {
  const { lang, t } = useI18n();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [contact, setContact] = useState({ name: "", email: "", company: "" });

  const questions = getQuizQuestions(lang);
  const total = questions.length;
  const isContactStep = step === total;
  const current = !isContactStep ? questions[step] : null;

  const reset = () => {
    setStep(0);
    setAnswers({});
    setResult(null);
    setContact({ name: "", email: "", company: "" });
    setSubmitting(false);
  };

  const handleClose = (val) => {
    if (!val) setTimeout(reset, 250);
    onOpenChange(val);
  };

  const choose = (option) => {
    if (!current) return;
    setAnswers((a) => ({ ...a, [current.id]: option }));
    setTimeout(() => setStep((s) => s + 1), 180);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payloadAnswers = Object.entries(answers).map(([qid, opt]) => ({
        question_id: qid,
        answer: opt.label,
        score: opt.score,
      }));
      const total_score = payloadAnswers.reduce((s, a) => s + a.score, 0);
      const r = await submitQuiz({
        name: contact.name,
        email: contact.email || null,
        company: contact.company,
        answers: payloadAnswers,
        total_score,
      });
      setResult(r);
      toast.success(lang === "en" ? "Analysis completed" : "Результаты отправлены", {
        description: lang === "en" ? "We will reach out within 24 hours" : "Мы свяжемся с вами в течение 24 часов.",
      });
    } catch (e) {
      toast.error(lang === "en" ? "Submission failed" : "Не удалось отправить", {
        description: lang === "en" ? "Please try again" : "Попробуйте ещё раз",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const progress = isContactStep ? 100 : Math.round((step / total) * 100);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="quiz-modal"
        className="bg-ink-surface border border-ink-text/10 text-ink-text max-w-2xl p-0 overflow-hidden"
      >
        <div className="relative p-7 sm:p-9">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold tracking-tight text-2xl">
              {t.quizModal.title}
            </DialogTitle>
            <DialogDescription className="text-muted mono text-xs uppercase tracking-widest mt-1">
              {t.quizModal.subtitle}
            </DialogDescription>
          </DialogHeader>

          {/* Progress */}
          <div className="mt-6 h-1 rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-brand to-brand-cyan transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="mt-2 mono text-[10px] uppercase tracking-widest text-muted">
            {result
              ? (lang === "en" ? "Complete" : "Готово")
              : isContactStep
              ? t.quizModal.stepContact
              : t.quizModal.stepCounter(step, total)}
          </div>

          {/* Body */}
          <div className="mt-7 min-h-[280px]">
            {result ? (
              <div data-testid="quiz-result" className="text-center py-6">
                <div className="inline-flex w-14 h-14 rounded-full bg-success/10 border border-success/40 items-center justify-center mb-5">
                  <CheckCircle size={26} weight="duotone" className="text-success" />
                </div>
                <div className="mono text-[10px] uppercase tracking-widest text-brand-cyan">
                  {t.quizModal.successReady}
                </div>
                <div className="font-heading text-3xl font-bold tracking-tight mt-2 mb-3">
                  {t.quizModal.successTitle}
                </div>
                <p className="text-sm text-muted max-w-md mx-auto leading-relaxed">
                  {t.quizModal.successDesc}
                </p>

                <div className="mt-7 flex flex-wrap gap-3 justify-center">
                  <button
                    onClick={() => {
                      handleClose(false);
                      onWantDemo();
                    }}
                    className="btn-primary rounded-lg px-5 py-2.5 font-semibold text-sm inline-flex items-center gap-2"
                  >
                    <Sparkle size={16} weight="fill" /> {t.quizModal.btnDemo}
                  </button>
                  <button
                    onClick={() => handleClose(false)}
                    className="btn-secondary rounded-lg px-5 py-2.5 font-semibold text-sm"
                  >
                    {t.quizModal.btnClose}
                  </button>
                </div>
              </div>
            ) : isContactStep ? (
              <div>
                <div className="mb-5">
                  <div className="font-heading font-semibold text-xl mb-1">
                    {t.quizModal.contactHeader}
                  </div>
                  <div className="text-sm text-muted">
                    {t.quizModal.contactDesc}
                  </div>
                </div>

                <div className="space-y-3.5">
                  <Field
                    label={t.quizModal.fieldName}
                    value={contact.name}
                    onChange={(v) => setContact((c) => ({ ...c, name: v }))}
                    testid="quiz-contact-name"
                  />
                  <Field
                    label={t.quizModal.fieldCompany}
                    value={contact.company}
                    onChange={(v) => setContact((c) => ({ ...c, company: v }))}
                    testid="quiz-contact-company"
                  />
                  <Field
                    label={t.quizModal.fieldEmail}
                    type="email"
                    value={contact.email}
                    onChange={(v) => setContact((c) => ({ ...c, email: v }))}
                    testid="quiz-contact-email"
                  />
                </div>

                <div className="mt-7 flex items-center justify-between">
                  <button
                    onClick={() => setStep((s) => s - 1)}
                    className="text-xs text-muted hover:text-ink-text mono"
                  >
                    ← {lang === "en" ? "Back" : "Назад"}
                  </button>
                  <button
                    onClick={submit}
                    disabled={submitting || !contact.name || !contact.email}
                    data-testid="quiz-submit"
                    className="btn-primary rounded-lg px-6 py-2.5 font-semibold inline-flex items-center gap-2 disabled:opacity-50"
                  >
                    {submitting ? t.quizModal.btnSubmitting : t.quizModal.btnSubmit}
                    <ArrowRight size={16} weight="bold" />
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="font-heading font-bold text-xl sm:text-2xl tracking-tight mb-5">
                  {current?.title}
                </div>
                <div className="space-y-2.5">
                  {current?.options.map((opt, i) => {
                    const sel = answers[current.id]?.label === opt.label;
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => choose(opt)}
                        data-testid={`quiz-opt-${current.id}-${i}`}
                        className={`w-full text-left p-4 rounded-xl border transition-all flex items-center justify-between group ${
                          sel
                            ? "border-brand-cyan bg-brand-cyan/10 text-ink-text"
                            : "border-ink-text/10 bg-ink hover:border-brand-cyan/50 hover:bg-white/[0.02]"
                        }`}
                      >
                        <span className="font-heading font-medium text-sm sm:text-base">
                          {opt.label}
                        </span>
                        <span
                          className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${
                            sel
                              ? "border-brand-cyan bg-brand-cyan text-ink"
                              : "border-ink-text/20 group-hover:border-brand-cyan"
                          }`}
                        >
                          {sel && <span className="w-2 h-2 rounded-full bg-ink" />}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {step > 0 && (
                  <button
                    onClick={() => setStep((s) => s - 1)}
                    className="mt-6 text-xs text-muted hover:text-ink-text mono"
                  >
                    ← {lang === "en" ? "Back" : "Назад"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, value, onChange, type = "text", testid }) => (
  <label className="block">
    <div className="mono text-[10px] uppercase tracking-widest text-muted mb-1.5">{label}</div>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full rounded-lg bg-ink border border-ink-text/10 px-3.5 py-2 text-sm outline-none focus:border-brand-cyan transition"
    />
  </label>
);

export default QuizModal;
